import React, { useState } from 'react'
import { string, bool } from 'prop-types'
import Type from '@/component/Primitive/Type'
import ButtonBase from '@/component/Primitive/ButtonBase'
import Icon from '@/component/Primitive/Icon'
import getGeoLocation from './lib/geolocation-retriever'
import fetchData from './lib/data-retriever'
import FieldTemplate from '@/component/Primitive/FieldTemplate'
import TextControl from '@/component/Primitive/TextControl'
import LoadingSpinner from '@/component/Primitive/Spinner'
import ButtonStandard from '@/component/Primitive/ButtonStandard'

import ShowTimes from '@/component/Common/ShowTimes'

import styles from './FilmTimes.module.scss'
import classNames from 'classnames'

const FilmTimes = ({ mxfReleaseId, layoutV2 }) => {
  const [postcode, setPostcode] = useState('')
  const [locationLoading, setLocationLoading] = useState(false)
  const [postcodeLoading, setPostcodeLoading] = useState(false)
  const [disablePostcodeInput, setDisablePostcodeInput] = useState(false)
  const [data, setShowTimeData] = useState([])
  const [showTimeError, setShowTimeError] = useState('')
  const [validationError, setValidationError] = useState('')
  const [noFilmTimesMessage, setNoFilmTimesMessage] = useState('')

  const defaultErrorMessage = 'Unable to get showtimes please try again later.'
  const showtimeNotFoundMessage =
    'Unable to find a cinema showing the film in your area.'
  const postcodeLabel =
    'Please enter your postcode below (or use your current location) so we can find the nearest cinemas showing this film.'
  const locationToolTip =
    'Click here to enable and use your browsers location information to find cinemas close to you.'

  const handleLocationSubmit = async (e) => {
    e.preventDefault()
    setLocationLoading(true)
    setDisablePostcodeInput(true)
    setNoFilmTimesMessage('')
    setShowTimeError('')
    setPostcode('')
    setValidationError('')
    setShowTimeData([])
    try {
      const position = await getGeoLocation()
      if (position) {
        const data = await fetchData(mxfReleaseId, position)
        const { errors, showtimes } = data || {}
        setLocationLoading(false)
        setDisablePostcodeInput(false)
        if (errors && errors.length > 0) {
          return setShowTimeError(defaultErrorMessage)
        }
        if (showtimes && showtimes.length === 0) {
          return setNoFilmTimesMessage(showtimeNotFoundMessage)
        }
        if (showtimes && showtimes.length > 0) {
          return setShowTimeData(data)
        }
      }
      setLocationLoading(false)
      setDisablePostcodeInput(false)
    } catch (err) {
      setShowTimeError(defaultErrorMessage)
      setLocationLoading(false)
      setDisablePostcodeInput(false)
    }
  }

  const handlePostcodeSubmit = async (e) => {
    e.preventDefault()
    setValidationError('')
    setNoFilmTimesMessage('')
    setShowTimeError('')
    setShowTimeData([])
    try {
      if (!postcode) {
        return setValidationError({ postcode: 'Postcode is required' })
      }
      const position = { postcode }
      if (position.postcode) {
        setPostcodeLoading(true)
        const data = await fetchData(mxfReleaseId, position)
        setPostcodeLoading(false)
        const { showtimes } = data || {}
        if (showtimes && showtimes.length === 0) {
          return setNoFilmTimesMessage(showtimeNotFoundMessage)
        }
        if (showtimes && showtimes.length > 0) {
          return setShowTimeData(data)
        }
      }
    } catch (err) {
      setPostcodeLoading(false)
      if (err.message.includes('Invalid postcode')) {
        return setValidationError({
          postcode: 'Postcode is invalid'
        })
      }
      setValidationError(defaultErrorMessage)
    }
  }

  return (
    <>
      <Type
        className={classNames(styles.Title, layoutV2 && styles.TitleLayoutV2)}
        as="h3"
        size="base-large"
      >
        Film showing times
      </Type>
      <span>{postcodeLabel}</span>
      <form
        onSubmit={handlePostcodeSubmit}
        className={(styles.Form, layoutV2 && styles.FormLayoutV2)}
      >
        <div
          className={classNames(
            styles.PostcodeInputContainer,
            layoutV2 && styles.PostcodeInputContainerLayoutV2
          )}
        >
          {!layoutV2 && (
            <div className={classNames(styles.PostcodeInput)}>
              <FieldTemplate
                controlName="postcode"
                hideLabel
                template="text"
                status={validationError.postcode && 'error'}
              >
                <TextControl
                  name="postcode"
                  disabled={disablePostcodeInput}
                  value={postcode}
                  placeholder="Enter postcode"
                  type="postcode"
                  onChange={(e) => setPostcode(e.target.value)}
                />
              </FieldTemplate>
            </div>
          )}
          {layoutV2 && (
            <>
              <input
                name="postcode"
                disabled={disablePostcodeInput}
                value={postcode}
                placeholder="Enter postcode"
                type="postcode"
                onChange={(e) => setPostcode(e.target.value)}
              />
              <button
                title={locationToolTip}
                className={styles.LocationButtonV2}
                onClick={handleLocationSubmit}
              >
                <Icon
                  type="location"
                  width={22}
                  height={22}
                  a11yText="Get Location"
                />
              </button>
            </>
          )}
          {!layoutV2 && !locationLoading && !postcodeLoading && (
            <ButtonBase
              title={locationToolTip}
              className={styles.LocationButton}
              onClick={handleLocationSubmit}
            >
              <Icon
                type="location"
                width={30}
                height={30}
                a11yText="Get Location"
              />
            </ButtonBase>
          )}
          {(locationLoading || postcodeLoading) && (
            <div className={styles.LocationButton}>
              <LoadingSpinner />
            </div>
          )}
        </div>
        {validationError && (
          <div className={styles.ValidationError}>
            {validationError.postcode}
          </div>
        )}

        {!postcodeLoading && (
          <ButtonStandard
            className={classNames(
              styles.PostcodeSubmit,
              layoutV2 && styles.PostcodeSubmitV2
            )}
            onClick={handlePostcodeSubmit}
            disabled={disablePostcodeInput}
            block
          >
            Submit
          </ButtonStandard>
        )}
        {showTimeError && (
          <div className={styles.WarningError}>{showTimeError}</div>
        )}
        {noFilmTimesMessage && (
          <div className={styles.InformationError}>{noFilmTimesMessage}</div>
        )}
      </form>
      {data && data.showtimes && (
        <ShowTimes showTimes={data.showtimes} layoutV2={layoutV2} />
      )}
    </>
  )
}

FilmTimes.propTypes = {
  mxfReleaseId: string,
  layoutV2: bool
}

export default FilmTimes
